'use client';

import classNames from '@sindresorhus/class-names';
import Star from '@web-catalog/public/img/star.svg';
import Image from 'next/image';
import Link from 'next/link';
import {useParams, useRouter} from 'next/navigation';
import {KeyboardEvent, memo, useCallback} from 'react';

import {OfferPriceMicrodata} from '@web-catalog/components/Microdata';

import {mockIconURL} from '@web-catalog/const';
import {MINIMUM_FOR_RATING} from '@web-catalog/const/numbers';

import {App, AppType, Orientation, Rating} from '@web-catalog/types';

import {useIsGamesPage} from '@web-catalog/hooks';
import {useActiveCategory} from '@web-catalog/hooks/useActiveCategory';
import {useCategoryToCategoryName} from '@web-catalog/hooks/useCategoryToCategoryName';
import {useOnImageError} from '@web-catalog/hooks/useOnImageError';

import {ROUTES} from '@web-catalog/routes';

import {Price} from '@web-catalog/ui-kit/Price';

import {emitEcommerceEvent, emitYmEvent} from '@web-catalog/utils/analytics';
import {useAppCardAnalytics} from '@web-catalog/utils/analytics/useAppCardAnalytics';
import {formatRating} from '@web-catalog/utils/formatRating';

import {Text} from '../Text';
import styles from './AppCard.module.scss';
import {PreviewScreens} from './PreviewScreens/PreviewScreens';

type AppCardBaseInfo = {
  packageName: App['packageName'];
  iconUrl: App['iconUrl'];
  price?: App['price'];
  appName: App['appName'];
  shortDescription: App['shortDescription'];
  fileUrls: App['fileUrls'];
  averageUserRating?: Rating['averageUserRating'];
  totalRatings?: Rating['totalRatings'];
  appId: App['appId'];
  appType: App['appType'];
  companyName?: App['companyName'];
  category?: App['category'];
  source?: string;
};

type Props = {
  app: AppCardBaseInfo;
  position: number;
  screensPreview?: Orientation;
  listName: string;
  logCustomAnalyticsOnClick?: (app: AppCardBaseInfo, position: number) => void;
  renderPriceAsMicrodata?: boolean;
  hoverOutside?: boolean;
};

export const AppCard = memo<Props>(
  ({
    app,
    position,
    screensPreview,
    listName,
    logCustomAnalyticsOnClick,
    renderPriceAsMicrodata = false,
    hoverOutside,
  }) => {
    const router = useRouter();
    const searchParams = useParams<{query: string}>();
    const isGamesPage = useIsGamesPage();
    const activeCategory = useActiveCategory();
    const appCategoryName = activeCategory?.category || 'main';
    const categoryNames = useCategoryToCategoryName();
    const {
      logAppsAppSelect,
      logAppCategoryAppSelect,
      logGamesGameSelect,
      logGameCategoryGameSelect,
      logSearchResultsClick,
    } = useAppCardAnalytics();

    const query = searchParams?.['query'] ?? '';

    const handleClickLink = useCallback(() => {
      if (logCustomAnalyticsOnClick) {
        logCustomAnalyticsOnClick(app, position);
      } else if (query) {
        logSearchResultsClick({
          query,
          position,
          appId: app.packageName,
          currentCategory: appCategoryName,
        });
      } else {
        if (isGamesPage) {
          logGameCategoryGameSelect(app.packageName, appCategoryName);
          logGamesGameSelect(app.packageName);
        } else {
          logAppCategoryAppSelect(app.packageName, appCategoryName);
          logAppsAppSelect(app.packageName);
        }
      }

      emitYmEvent({eventName: 'ecommerce_select_item'});

      emitEcommerceEvent({
        eventName: 'select_item',
        app: {
          packageName: app.packageName,
          appId: app.appId,
          developer: app.companyName,
          type: app.appType,
          category: app.category
            ? (categoryNames[app.category] ?? app.category)
            : undefined,
          price: app.price ?? 0,
        },
        placement: {listName, position},
      });
    }, [
      logCustomAnalyticsOnClick,
      query,
      app,
      categoryNames,
      listName,
      position,
      appCategoryName,
      isGamesPage,
      logGameCategoryGameSelect,
      logAppCategoryAppSelect,
      logGamesGameSelect,
      logAppsAppSelect,
      logSearchResultsClick,
    ]);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Enter') {
        event.preventDefault();
        handleClickLink();
        router.push(ROUTES.app.app(app.packageName));
      }
    };

    const onImageError = useOnImageError();
    const showPrice = !!app.price && !renderPriceAsMicrodata;
    const reviewsCount =
      Number(app.totalRatings) >= MINIMUM_FOR_RATING ? app.totalRatings : 0;
    const averageUserRating = reviewsCount ? app.averageUserRating : 0;

    return (
      <Link
        id={`app-card-link-${app.appId}`}
        data-testid="AppCardLink"
        className={classNames(styles.root, hoverOutside && styles.hoverOutside)}
        href={ROUTES.app.app(app.packageName)}
        onClick={handleClickLink}
        prefetch={false}
      >
        <div
          data-testid="AppCardContainer"
          onKeyDown={handleKeyDown}
          itemScope
          itemType="https://schema.org/SoftwareApplication"
        >
          <meta itemProp="operatingSystem" content="Android" />
          <meta
            itemProp="applicationCategory"
            content={app.appType === AppType.GAMES ? 'Игры' : 'Приложения'}
          />
          {app.price !== undefined && renderPriceAsMicrodata && (
            <OfferPriceMicrodata price={app.price} />
          )}
          <div className={styles.innerBox}>
            <div className={styles.appIcon}>
              <Image
                className={styles.image}
                src={app.iconUrl ?? mockIconURL}
                alt={`${app.appName} логотип`}
                fill
                onError={onImageError}
              />
            </div>
            <div className={styles.infoBlock}>
              <Text
                typography="subheader-2"
                overflow="ellipsis"
                maxLines={1}
                itemProp="name"
                as="p"
              >
                {app.appName}
              </Text>
              <Text
                typography="content-s"
                overflow="ellipsis"
                maxLines={1}
                itemProp="description"
                as="p"
              >
                {app.shortDescription}
              </Text>
              <div className={styles.appMisc}>
                {showPrice && (
                  <Text
                    className={styles.price}
                    typography="content-s"
                    color="primary-main"
                    as="p"
                    data-testid="AppCard_price"
                  >
                    <Price price={app.price} />
                  </Text>
                )}

                {!!averageUserRating && !!reviewsCount && (
                  <span
                    itemScope={true}
                    itemType="https://schema.org/AggregateRating"
                    itemProp="aggregateRating"
                    className={styles.rating}
                  >
                    <meta
                      itemProp="ratingValue"
                      content={`${averageUserRating}`}
                    />
                    <meta itemProp="reviewCount" content={`${reviewsCount}`} />
                    <Star />
                    <Text
                      typography="content-s"
                      color={'neutral-500'}
                      data-testid="AppCard_rating"
                      data-rating={averageUserRating || 0}
                    >
                      {formatRating(Number(averageUserRating || 0))}
                    </Text>
                  </span>
                )}
              </div>
            </div>
          </div>
          {screensPreview && (
            <PreviewScreens
              orientation={screensPreview}
              screens={app.fileUrls}
            />
          )}
        </div>
      </Link>
    );
  },
);

AppCard.displayName = 'AppCard';
